/* eslint-disable complexity */
import React, { FC, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Button as MuiButton, IconButton, Typography, CircularProgress, SxProps, Theme } from '@mui/material';
import { IsNotEmpty, isPhoneNumber, IsEmail, MinLength } from 'class-validator';
import { getUserEmail, TokensDto, UpdateUserDto, UserDto, VerificationInstance } from '@sweatpals/shared';
import { CoreLabeledCheckbox, Button, clsx } from '@sweatpals/react-ui-kit';
import { CorePhoneInput } from 'components/ui-v3/core-phone-input';
import { useFormik } from 'formik';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { add } from 'date-fns';
import { useStyles } from '../../../../utils/hooks/useStyles';
import { signInFormStyles } from './signin-form.styles';
import Showable from '../../../ui-v2/showable';
import { authService, usersService } from '../../../../services';
import { authStore } from '../../../../stores';
import GoogleAuthButton from '../../../../components/buttons/google-auth.button';
import { ArrowBackIcon, GoogleIcon } from '../../../icons';
import { CoreTextField } from '../../core-textfield';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { isSmsEnabled } from '../../../../constants';
import * as fbq from '../../../../lib/fb-pixel';

export enum LoginStepEnum {
	EnterPhone = 1,
	VerifyCode = 2,
	AdditionalData = 3,
}

class AdditionalDataValidator {
	@MinLength(3, { message: 'Full name is too short' })
	fullName: string;

	@IsNotEmpty({ message: 'Email is required' })
	@IsEmail(undefined, { message: 'Invalid email' })
	receiptsEmail: string;
}

const updateUserResolver = classValidatorResolver(AdditionalDataValidator);

const hasRequiredData = (user: UserDto) => user.fullName && user.receiptsEmail;

export interface SignInFormProps {
	formTitle?: string;
	customWebCheckoutMainColor?: string;
	sx?: SxProps<Theme>;
	contentSx?: SxProps<Theme>;
	footerSx?: SxProps<Theme>;
	onSuccess(): void | Promise<void>;
	onBackButtonClick?(): void;
	onError?(err: string): void | Promise<void>;
}

const SignInForm: FC<SignInFormProps> = observer((props) => {
	const {
		formTitle,
		customWebCheckoutMainColor,
		sx,
		contentSx,
		footerSx,
		onSuccess,
		onError,
		onBackButtonClick
	} = props;
	const [loginStep, setLoginStep] = useState<LoginStepEnum>(LoginStepEnum.EnterPhone);

	const [isLoading, setIsLoading] = useState(false);
	const [phoneCodeError, setPhoneCodeError] = useState('');

	const [resendCodeTimer, setResetCodeTimer] = useState<number | null>(null);

	const [isSignIn, setIsSignIn] = useState<boolean | null>(null);
	const [verification, setVerification] = useState<null | VerificationInstance>(null);

	const googleButtonRef = useRef<HTMLElement>(null);

	const styles = useStyles(signInFormStyles, customWebCheckoutMainColor);

	useEffect(() => {
		if (loginStep !== LoginStepEnum.VerifyCode) {
			return;
		}

		const interval = setInterval(() => {
			setResetCodeTimer(value => (value > 0 ? value - 1 : null));
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [loginStep]);

	const onResendCode = useCallback(
		async (phoneNumber: string) => {
			if (isLoading) {
				return;
			}

			try {
				setResetCodeTimer(60);

				await authService.signInWithPhone(phoneNumber);

				setLoginStep(LoginStepEnum.VerifyCode);
			} catch (e) {
				onError?.(e.response?.data?.message || e.message);
			}
		},
		[onError, isLoading]
	);

	const onPhoneSubmit = useCallback(async (phoneNumber: string) => {
		setIsLoading(true);

		const { verification, isSignIn } = await authService.signInOrSignUpWithPhone(phoneNumber);

		setVerification(verification);
		setIsSignIn(isSignIn);

		setIsLoading(false);
		setResetCodeTimer(60);

		window.scrollTo(0, 0);
	}, []);

	const verificationCodeSubmit = useCallback(async (phoneNumber: string, verificationCode: string) => {
		if ((!verification && isSmsEnabled) || isSignIn === null) {
			return;
		}

		let tokens: TokensDto = null;

		try {
			setIsLoading(true);

			if (isSignIn) {
				tokens = await authService.signInWithPhoneVerification(phoneNumber, verificationCode);
			} else {
				tokens = await authService.signUpWithPhoneVerification(phoneNumber, verificationCode);
			}
		} catch (error) {
			if (error.response?.status === 400) {
				onError?.('Invalid verification code');
				setPhoneCodeError('Invalid verification code');
			}
		} finally {
			setIsLoading(false);
		}

		if (!tokens) {
			return;
		}

		try {
			setIsLoading(true);

			await authStore.setAuth(tokens.access_token, tokens.refresh_token);

			// if sign up then require additional data
			if (!isSignIn && !hasRequiredData(authStore.user)) {
				setLoginStep(LoginStepEnum.AdditionalData);
			} else {
				if (!isSignIn) {
					// track registration
					fbq.trackSweatPals('CompleteRegistration', {
						method: 'Google',
						external_id: authStore.user.id,
						email: getUserEmail(authStore.user),
						fullName: authStore.user.fullName,
						userName: authStore.user.userName,
					});
				}
				await onSuccess();
			}
		} catch (error) {
			onError?.(error.response?.data?.message || error.message);
		} finally {
			setIsLoading(false);
		}

		window.scrollTo(0, 0);
	}, [isSignIn, onError, onSuccess, verification]);

	const { values, errors, handleSubmit, handleChange, setFieldValue, setFieldError, validateField } = useFormik({
		initialValues: {
			phoneNumber: '',
			verificationCode: '',
			aggreeTerms: true,
		},
		validate: values => {
			const errors: { [key: string]: string } = {};

			const fullPhone = `${values.phoneNumber}`;
			const digitsRegexp = /^\+[\d\s]+$/;

			if (!values.phoneNumber) {
				errors.phoneNumber = 'Phone number is required';
			} else if (!isPhoneNumber(fullPhone, null) || !digitsRegexp.test(fullPhone)) {
				errors.phoneNumber = 'Invalid phone number';
			}

			return errors;
		},
		onSubmit: async values => {
			const phoneFullNumber = `${values.phoneNumber}`;

			if (loginStep === LoginStepEnum.EnterPhone) {
				await onPhoneSubmit(phoneFullNumber);
				setLoginStep(LoginStepEnum.VerifyCode);
			} else if (loginStep === LoginStepEnum.VerifyCode) {
				await verificationCodeSubmit(phoneFullNumber, values.verificationCode);
			}
		},
	});

	const {
		reset,
		watch,
		formState,
		setValue,
		handleSubmit: handleSubmitData,
	} = useForm<UpdateUserDto>({
		defaultValues: {
			fullName: '',
			receiptsEmail: '',
		},
		resolver: updateUserResolver,
	});

	const formValues = watch();

	useEffect(() => {
		reset({
			fullName: authStore.user?.fullName ?? '',
			receiptsEmail: authStore.user?.receiptsEmail ?? '',
			login: authStore.user?.login ?? '',
		});
	}, [authStore.user]);

	const handleSubmitAdditionalData = useCallback(
		async (data: UpdateUserDto) => {
			try {
				setIsLoading(true);

				await usersService.updateUser(data);

				await authStore.loadCurrentUser();

				// track registration
				fbq.trackSweatPals('CompleteRegistration', {
					method: 'Google',
					external_id: authStore.user.id,
					email: getUserEmail(authStore.user),
					fullName: authStore.user.fullName,
					userName: authStore.user.userName
				});
				await onSuccess();
			} catch (error) {
				onError?.(error.response?.data?.message || error.message);
			} finally {
				setIsLoading(false);
			}
		},
		[onError]
	);

	useEffect(() => {
		if (phoneCodeError) {
			setFieldError('verificationCode', phoneCodeError);
		}
	}, [phoneCodeError, setFieldError]);

	const phoneFullNumber = useMemo(() => `${values.phoneNumber}`, [values.phoneNumber]);

	const handleGoogleSignIn = useCallback(
		async response => {
			try {
				setIsLoading(true);

				const idToken = response.credential;

				const tokens = await authService.signInWithGoogleToken(idToken);

				if (tokens) {
					await authStore.setAuth(tokens.access_token, tokens.refresh_token);

					// if account was newly created
					const isSignIn = add(new Date(authStore.user.createdAt), { minutes: 15 }) < new Date();
					setIsSignIn(isSignIn);

					if (!isSignIn && !hasRequiredData(authStore.user)) {
						setLoginStep(LoginStepEnum.AdditionalData);
					} else {
						if (!isSignIn) {
							// track registration
							fbq.trackSweatPals('CompleteRegistration', {
								method: 'Google',
								external_id: authStore.user.id,
								email: getUserEmail(authStore.user),
								fullName: authStore.user.fullName,
								userName: authStore.user.userName
							});
						}
						await onSuccess();
					}
				}
			} catch (error) {
				onError?.(error.response?.data?.message || error.message);
			} finally {
				setIsLoading(false);
			}

			window.scrollTo(0, 0);
		},
		[onError, onSuccess]
	);

	const setGoogleButtonRef = useCallback(ref => {
		googleButtonRef.current = ref;
	}, []);

	const handleGoogleButtonClick = useCallback(() => {
		googleButtonRef.current?.click();
	}, []);

	const handleBackButtonClick = useCallback(() => {
		if (onBackButtonClick && loginStep === LoginStepEnum.EnterPhone) {
			onBackButtonClick();

			return;
		}

		setLoginStep(LoginStepEnum.EnterPhone);
		setVerification(null);
		setFieldValue('verificationCode', '');
	}, [setFieldValue, loginStep]);

	return (
		<>
			<Box
				sx={clsx(styles.root, sx)}
				component='form'
				onSubmit={
					loginStep === LoginStepEnum.AdditionalData ? handleSubmitData(handleSubmitAdditionalData) : handleSubmit
				}
			>
				<Box sx={clsx(styles.content, contentSx)}>
					<Showable
						show={
							(onBackButtonClick && loginStep === LoginStepEnum.EnterPhone)
							|| loginStep === LoginStepEnum.VerifyCode
						}
					>
						<IconButton sx={styles.backButton} disabled={isLoading} onClick={handleBackButtonClick}>
							<ArrowBackIcon />
						</IconButton>
					</Showable>

					<Box sx={styles.header}>
						<Typography sx={styles.title}>
							{loginStep === LoginStepEnum.AdditionalData ? 'Welcome to SweatPals' : formTitle ? formTitle : 'Login:'}
						</Typography>
					</Box>

					<Showable show={loginStep === LoginStepEnum.EnterPhone}>
						<GoogleAuthButton
							show={false}
							setButtonRef={setGoogleButtonRef}
							onSuccess={handleGoogleSignIn}
							theme='outline'
							shape='circle'
						/>
						<MuiButton sx={styles.loginTypeButton} disabled={isLoading} onClick={handleGoogleButtonClick}>
							<GoogleIcon />
							<Typography sx={styles.loginTypeButtonText}>Continue with Google</Typography>
						</MuiButton>
						<Box sx={styles.separatorRow}>
							<Box sx={styles.separatorLine} />
							<Typography sx={styles.separatorText}>OR</Typography>
							<Box sx={styles.separatorLine} />
						</Box>
						<Box sx={{ display: 'flex', gap: '16px' }}>
							<CorePhoneInput
								controlId='phone'
								id='phone-input'
								fullWidth
								placeholder='Phone Number'
								name='phoneNumber'
								disabled={isLoading}
								value={values.phoneNumber}
								defaultCountry='US'
								forceCallingCode
								MenuProps={{ disableAutoFocusItem: true }}
								sx={styles.textField}
								onChange={phoneNumber => {
									setFieldValue('phoneNumber', phoneNumber);
								}}
								onBlur={() => {
									validateField('phoneNumber');
								}}
								error={!!errors.phoneNumber}
								helperText={errors.phoneNumber}
							/>
						</Box>
					</Showable>

					<Showable show={loginStep === LoginStepEnum.VerifyCode}>
						<Typography sx={styles.description}>
							{`Enter code that we have sent you on `}
							<Box component='span' fontWeight={600}>
								{values.phoneNumber}
							</Box>
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
							<CoreTextField
								fullWidth
								placeholder='Code'
								disabled={isLoading}
								sx={styles.textField}
								value={values.verificationCode}
								name='verificationCode'
								autoComplete='one-time-code'
								onChange={handleChange}
								error={!!errors.verificationCode}
								helperText={errors.verificationCode}
							/>
							<Typography
								sx={{
									fontFamily: 'Montserrat',
									fontStyle: 'normal',
									fontWeight: 400,
									fontSize: '13px',
									lineHeight: '16px',
									color: '#FFFFFF',
								}}
							>
								{resendCodeTimer ? (
									<Box component='span'>Resend in {resendCodeTimer}.</Box>
								) : (
									<Box
										component='span'
										sx={
											!isLoading
												? {
														'&:hover': {
															cursor: 'pointer',
															textDecoration: 'underline',
														},
												  }
												: undefined
										}
										onClick={() => {
											onResendCode(phoneFullNumber);
										}}
									>
										Resend code
									</Box>
								)}
							</Typography>
						</Box>
					</Showable>

					<Showable show={loginStep === LoginStepEnum.AdditionalData && authStore.user}>
						{!authStore.user?.fullName && (
							<CoreTextField
								value={formValues.fullName}
								onChange={e => {
									setValue('fullName', e.target.value);
								}}
								onBlur={e => {
									setValue('fullName', e.target.value);
								}}
								name="fullName"
								label='Name'
								requiredMark
								placeholder='Your full name'
								autoComplete='name'
								disabled={isLoading}
								sx={styles.textField}
								error={!!formState.errors.fullName}
								helperText={formState.errors.fullName?.message}
							/>
						)}
						{!authStore.user?.receiptsEmail && (
							<CoreTextField
								value={formValues.receiptsEmail}
								onChange={e => {
									setValue('receiptsEmail', e.target.value);
								}}
								onBlur={e => {
									setValue('receiptsEmail', e.target.value);
								}}
								name="receiptsEmail"
								label='Email'
								requiredMark
								placeholder='Enter email'
								autoComplete='email'
								disabled={isLoading}
								sx={styles.textField}
								error={!!formState.errors.receiptsEmail}
								helperText={formState.errors.receiptsEmail?.message}
							/>
						)}
					</Showable>
				</Box>
				<Box sx={clsx(styles.footer, footerSx)}>
					<Button sx={styles.submitButton} disabled={isLoading || !values.aggreeTerms} type='submit'>
						{isLoading ? <CircularProgress size={30} /> : 'Continue'}
					</Button>
				</Box>
			</Box>

			<Showable show={loginStep === LoginStepEnum.EnterPhone}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						mt: '8px',
					}}
				>
					<CoreLabeledCheckbox
						id='privacy-policy'
						checked={values.aggreeTerms}
						onChange={handleChange}
						name='aggreeTerms'
						label={
							<>
								By continuing, you agree to our{' '}
								<a href='/privacy-policy.html' target='_blank'>
									Privacy Policy
								</a>{' '}
								and{' '}
								<a href='/terms.html' target='_blank'>
									Terms & Conditions
								</a>
							</>
						}
						labelSx={{
							'& .MuiTypography-root, & a': {
								color: 'rgba(255, 255, 255, 0.7)',
							},
						}}
					/>
				</Box>
			</Showable>
		</>
	);
});

export default SignInForm;
